import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { AppIcon } from '../ui/icon'
import { mdiCalendarClock, mdiCalendarMonthOutline, mdiCheck, mdiCheckCircleOutline, mdiChevronDown, mdiChevronUp, mdiClockAlertOutline, mdiFormatListBulleted, mdiPencilOutline, mdiPlus, mdiTrashCanOutline, mdiUnfoldMoreHorizontal } from '@mdi/js'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Label } from '../ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '../ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import { apiBaseUrl } from '../../api/client'
import { InterventionDetailDrawer } from './InterventionDetailDrawer'
import { InterventionCalendar } from './InterventionCalendar'
import type { CalendarFilter } from './calendar-model'
import { Can } from '../../contexts/PermissionsContext'
import {
  useInterventions,
  useCreateIntervention,
  useUpdateIntervention,
  useDeleteIntervention,
  type Intervention,
  type InterventionKind,
  type InterventionStatus,
  type CreateInterventionInput,
} from '../../hooks/useInterventions'
import { EmptyState } from '../ui/empty-state'
import { SECTION_ICONS } from '../../lib/section-icons'

// Sortable columns of the interventions table (every column except the
// trailing actions cell).
type SortKey = 'reference' | 'title' | 'kind' | 'status' | 'billing' | 'scheduled' | 'performed' | 'provider' | 'cost'

const KIND_VARIANT: Record<InterventionKind, 'info' | 'warning' | 'secondary'> = {
  preventive: 'info',
  corrective: 'warning',
  works: 'secondary',
}
const STATUS_VARIANT: Record<
  InterventionStatus,
  'outline' | 'info' | 'warning' | 'success' | 'secondary'
> = {
  // A generated occurrence nobody has ordered yet reads as neutral: it is not a
  // demand on anyone, and colouring it would claim otherwise.
  planned: 'secondary',
  requested: 'outline',
  scheduled: 'info',
  assigned: 'info',
  in_progress: 'warning',
  done: 'success',
  cancelled: 'secondary',
}
const BILLING_VARIANT: Record<string, 'secondary' | 'warning' | 'success' | 'outline'> = {
  included: 'secondary',
  to_bill: 'warning',
  billed: 'success',
  not_applicable: 'outline',
}

const KINDS: InterventionKind[] = ['preventive', 'corrective', 'works']
const STATUSES: InterventionStatus[] = [
  'planned',
  'requested',
  'scheduled',
  'assigned',
  'in_progress',
  'done',
  'cancelled',
]

interface ContractLite {
  uid: string
  name?: string
  code?: string
  billing_model?: string
}

function fmtDate(s?: string | null): string {
  if (!s) return '—'
  return new Date(s).toLocaleDateString()
}
function fmtCost(amount?: number | null, currency?: string | null): string {
  if (amount == null) return '—'
  const v = amount / 100
  // No euro fallback: an amount without its unit is incomplete, an amount
  // under the wrong symbol is false. Only the second one misleads.
  return currency
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(v)
    : v.toLocaleString(undefined, { maximumFractionDigits: 2 })
}
function toISO(local: string): string | null {
  if (!local) return null
  const d = new Date(local)
  return isNaN(d.getTime()) ? null : d.toISOString()
}

interface Props {
  org: string
  assetUid: string
  assetTypeCode?: string
}

export function InterventionsTab({ org, assetUid }: Props) {
  const { t } = useTranslation('assets')
  const [kindFilter, setKindFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Intervention | null>(null)
  const [toDelete, setToDelete] = useState<Intervention | null>(null)
  const [detailUid, setDetailUid] = useState<string | null>(null)
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null)
  // The calendar is the tab's main view, the list one click away; both ride
  // in the URL so a link reopens the same view (spec 2026-10-25-22).
  const [view, setView] = useState<'calendar' | 'list'>(() =>
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('iv_view') === 'list' ? 'list' : 'calendar')
  const [calendarFilter, setCalendarFilter] = useState<CalendarFilter>(() => {
    const v = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('iv_cat') : null
    return v === 'maintenance' || v === 'check' || v === 'other' ? v : 'all'
  })
  useEffect(() => {
    const url = new URL(window.location.href)
    if (view === 'list') url.searchParams.set('iv_view', 'list')
    else url.searchParams.delete('iv_view')
    if (calendarFilter !== 'all') url.searchParams.set('iv_cat', calendarFilter)
    else url.searchParams.delete('iv_cat')
    window.history.replaceState(window.history.state, '', url.toString())
  }, [view, calendarFilter])

  const toggleSort = (key: SortKey) => {
    setSort((prev) => (prev?.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' }))
  }

  const sortIcon = (key: SortKey) => {
    if (sort?.key !== key) return <AppIcon path={mdiUnfoldMoreHorizontal} size="xs" tone="muted" className="opacity-40" />
    return sort.dir === 'asc'
      ? <AppIcon path={mdiChevronUp} size="xs" tone="muted" />
      : <AppIcon path={mdiChevronDown} size="xs" tone="muted" />
  }

  const filters = useMemo(
    () => ({ kind: kindFilter, status: statusFilter, subtree: true }),
    [kindFilter, statusFilter],
  )
  const { data: interventions = [], isLoading, isError } = useInterventions(
    org,
    assetUid,
    filters,
  )
  // The calendar reads every intervention, whatever the list filters say.
  // Twenty-four months of a whole building: a page of 200 would silently
  // drop events (Codex review), so the calendar asks for a large one.
  const allFilters = useMemo(() => ({ subtree: true, limit: 1000 }), [])
  const allQuery = useInterventions(org, assetUid, allFilters)

  // Client-side sort of the rows the server returned, applied on top of the
  // kind/status filters so the visible table reorders instantly. Enum columns
  // sort on their translated label, so the order matches what is on screen
  // rather than the underlying code. Rows with no date, provider or cost sink
  // to the bottom in both directions — an empty cell is not a small value.
  const sortedInterventions = useMemo(() => {
    if (!sort) return interventions
    const value = (iv: Intervention): string | number | null => {
      switch (sort.key) {
        case 'reference': return iv.reference?.toLowerCase() ?? null
        case 'title': return iv.title?.toLowerCase() ?? null
        case 'kind': return t(`interventions.kind.${iv.kind}`, { defaultValue: iv.kind }).toLowerCase()
        case 'status': return t(`interventions.status.${iv.status}`, { defaultValue: iv.status }).toLowerCase()
        case 'billing': return iv.billing_status
          ? t(`interventions.billing.${iv.billing_status}`, { defaultValue: iv.billing_status }).toLowerCase()
          : null
        case 'scheduled': return iv.scheduled_at || null
        case 'performed': return iv.performed_at || null
        case 'provider': return iv.provider?.name?.toLowerCase() ?? null
        case 'cost': return iv.cost_amount ?? null
      }
    }
    return [...interventions].sort((a, b) => {
      const av = value(a)
      const bv = value(b)
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sort.dir === 'asc' ? cmp : -cmp
    })
  }, [interventions, sort, t])

  // One sortable header cell — keeps the nine columns declarative below.
  const sortableTh = (key: SortKey, label: string) => (
    <th
      className="px-3 py-2"
      aria-sort={sort?.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => toggleSort(key)}
        className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
        data-testid={`sort-intervention-${key}`}
      >
        {label}
        {sortIcon(key)}
      </button>
    </th>
  )

  const { data: contracts = [] } = useQuery({
    queryKey: ['contracts-lite', org],
    queryFn: async () => {
      const token = localStorage.getItem('token')
      const res = await fetch(
        `${apiBaseUrl}/api/v1/organizations/${org}/contracts?limit=200`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      )
      if (!res.ok) return [] as ContractLite[]
      const body = await res.json()
      return (body.data || []) as ContractLite[]
    },
    enabled: !!org,
  })

  const createMut = useCreateIntervention(org, assetUid)
  const updateMut = useUpdateIntervention(org, assetUid)
  const deleteMut = useDeleteIntervention(org, assetUid)

  // Metrics
  const now = Date.now()
  const metrics = useMemo(() => {
    let upcoming = 0
    let overdue = 0
    let done12m = 0
    let cost = 0
    const yearAgo = now - 365 * 24 * 3600 * 1000
    for (const iv of interventions) {
      const open = iv.status !== 'done' && iv.status !== 'cancelled'
      const sched = iv.scheduled_at ? new Date(iv.scheduled_at).getTime() : null
      if (open && sched != null && sched < now) overdue++
      else if (open) upcoming++
      if (iv.status === 'done' && iv.performed_at) {
        const p = new Date(iv.performed_at).getTime()
        if (p >= yearAgo) {
          done12m++
          if (iv.cost_amount) cost += iv.cost_amount
        }
      }
    }
    return { upcoming, overdue, done12m, cost }
  }, [interventions, now])

  const openCreate = () => {
    setEditing(null)
    setDialogOpen(true)
  }
  const openEdit = (iv: Intervention) => {
    setEditing(iv)
    setDialogOpen(true)
  }

  return (
    <div className="space-y-6" data-testid="interventions-tab">
      {/* The preventive schedule sits above the log rather than in a tab of its
          own (spec 2026-08-24-01, lot 4): an occurrence *is* an intervention, and
          splitting one object across two screens is how a registry starts
          disagreeing with itself. */}
      <div className="flex justify-end">
        <div className="inline-flex rounded-md border border-border p-0.5" role="group" aria-label={t('interventions:view.calendar')}>
          {(['calendar', 'list'] as const).map((v) => (
            <Button
              key={v}
              type="button"
              size="sm"
              variant={view === v ? 'secondary' : 'ghost'}
              className="h-7"
              aria-pressed={view === v}
              onClick={() => setView(v)}
              data-testid={`interventions-view-${v}`}
            >
              <AppIcon path={v === 'calendar' ? mdiCalendarMonthOutline : mdiFormatListBulleted} size="sm" className="md:mr-1" />
              <span className="hidden md:inline">{t(`interventions:view.${v}`)}</span>
            </Button>
          ))}
        </div>
      </div>

      {view === 'calendar' && (
        <InterventionCalendar
          org={org}
          assetUid={assetUid}
          interventions={allQuery.data ?? []}
          isLoading={allQuery.isLoading}
          filter={calendarFilter}
          onFilterChange={setCalendarFilter}
          onSelect={setDetailUid}
        />
      )}

      {/* Metrics bar */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricCard
          icon={mdiCalendarClock}
          tone="info"
          value={metrics.upcoming}
          label={t('interventions.metrics.upcoming', { defaultValue: 'À venir' })}
          testid="intervention-stat-upcoming"
        />
        <MetricCard
          icon={mdiClockAlertOutline}
          tone="warning"
          value={metrics.overdue}
          label={t('interventions.metrics.overdue', { defaultValue: 'En retard' })}
          testid="intervention-stat-overdue"
        />
        <MetricCard
          icon={mdiCheckCircleOutline}
          tone="success"
          value={metrics.done12m}
          label={t('interventions.metrics.done_12m', { defaultValue: 'Réalisées (12 mois)' })}
          testid="intervention-stat-done"
        />
        <MetricCard
          icon={SECTION_ICONS.expenses}
          tone="muted"
          value={fmtCost(metrics.cost)}
          label={t('interventions.metrics.cost_total', { defaultValue: 'Coût cumulé' })}
          testid="intervention-stat-cost"
        />
      </div>

      {/* Filters + action */}
      <div className="flex flex-wrap items-center gap-2">
        {view === 'list' && (<>
        <Select value={kindFilter || 'all'} onValueChange={(v) => setKindFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[170px]" data-testid="intervention-kind-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('interventions.filter.all_kinds', { defaultValue: 'Tous les types' })}</SelectItem>
            {KINDS.map((k) => (
              <SelectItem key={k} value={k}>
                {t(`interventions.kind.${k}`, { defaultValue: k })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[170px]" data-testid="intervention-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('interventions.filter.all_statuses', { defaultValue: 'Tous les statuts' })}</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`interventions.status.${s}`, { defaultValue: s })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        </>)}
        <div className="ml-auto">
          {/* `exact`: a contractor's `interventions.write_assigned` cannot create
              a row that has no provider yet, so the widened check would render
              a button the backend refuses with a 404. */}
          <Can perm="interventions.write" exact>
            <Button size="sm" onClick={openCreate} data-testid="plan-intervention-btn">
              <AppIcon path={mdiPlus} size="sm" className="mr-1" />
              {t('interventions.action.plan', { defaultValue: 'Planifier une intervention' })}
            </Button>
          </Can>
        </div>
      </div>

      {/* List */}
      {view === 'list' && (isLoading ? (
        <div className="animate-pulse space-y-2" data-testid="interventions-loading">
          <div className="h-10 rounded bg-muted" />
          <div className="h-10 rounded bg-muted" />
        </div>
      ) : isError ? (
        <p className="text-destructive">{t('interventions.error', { defaultValue: 'Erreur de chargement.' })}</p>
      ) : interventions.length === 0 ? (
        <EmptyState
          icon={SECTION_ICONS.interventions}
          title={t('interventions.empty.title', { defaultValue: 'Aucune intervention — planifiez la première.' })}
          data-testid="interventions-empty"
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                {sortableTh('reference', t('interventions.col.reference', { defaultValue: 'Réf.' }))}
                {sortableTh('title', t('interventions.col.title', { defaultValue: 'Intitulé' }))}
                {sortableTh('kind', t('interventions.col.kind', { defaultValue: 'Type' }))}
                {sortableTh('status', t('interventions.col.status', { defaultValue: 'Statut' }))}
                {sortableTh('billing', t('interventions.col.billing', { defaultValue: 'Facturation' }))}
                {sortableTh('scheduled', t('interventions.col.scheduled', { defaultValue: 'Planifiée' }))}
                {sortableTh('performed', t('interventions.col.performed', { defaultValue: 'Réalisée' }))}
                {sortableTh('provider', t('interventions.col.provider', { defaultValue: 'Prestataire' }))}
                {sortableTh('cost', t('interventions.col.cost', { defaultValue: 'Coût' }))}
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {sortedInterventions.map((iv) => (
                <tr key={iv.uid} className="border-t border-border hover:bg-muted/30" data-testid={`intervention-row-${iv.uid}`}>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{iv.reference}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="text-left hover:underline"
                      data-testid={`intervention-open-${iv.uid}`}
                      onClick={() => setDetailUid(iv.uid)}
                    >
                      {iv.title}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={KIND_VARIANT[iv.kind]}>{t(`interventions.kind.${iv.kind}`, { defaultValue: iv.kind })}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={STATUS_VARIANT[iv.status]}>{t(`interventions.status.${iv.status}`, { defaultValue: iv.status })}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={BILLING_VARIANT[iv.billing_status] || 'outline'}>
                      {t(`interventions.billing.${iv.billing_status}`, { defaultValue: iv.billing_status })}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{fmtDate(iv.scheduled_at)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{fmtDate(iv.performed_at)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{iv.provider?.name || '—'}</td>
                  <td className="px-3 py-2">{fmtCost(iv.cost_amount, iv.cost_currency)}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      {iv.status !== 'done' && iv.status !== 'cancelled' && (
                        <Button
                          variant="outline"
                          size="icon"
                          aria-label={t('interventions.action.complete', { defaultValue: 'Marquer réalisée' })}
                          title={t('interventions.action.complete', { defaultValue: 'Marquer réalisée' })}
                          data-testid={`intervention-complete-${iv.uid}`}
                          onClick={() => updateMut.mutate({ uid: iv.uid, status: 'done' })}
                        >
                          <AppIcon path={mdiCheck} size="sm" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="icon"
                        aria-label={t('interventions.action.edit', { defaultValue: 'Éditer' })}
                        title={t('interventions.action.edit', { defaultValue: 'Éditer' })}
                        data-testid={`intervention-edit-${iv.uid}`}
                        onClick={() => openEdit(iv)}
                      >
                        <AppIcon path={mdiPencilOutline} size="sm" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="icon"
                        aria-label={t('interventions.action.delete', { defaultValue: 'Supprimer' })}
                        title={t('interventions.action.delete', { defaultValue: 'Supprimer' })}
                        data-testid={`intervention-delete-${iv.uid}`}
                        onClick={() => setToDelete(iv)}
                      >
                        <AppIcon path={mdiTrashCanOutline} size="sm" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <InterventionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        assetUid={assetUid}
        contracts={contracts}
        editing={editing}
        onCreate={(input) => createMut.mutate(input, { onSuccess: () => setDialogOpen(false) })}
        onUpdate={(uid, input) => updateMut.mutate({ uid, ...input }, { onSuccess: () => setDialogOpen(false) })}
      />

      <InterventionDetailDrawer
        org={org}
        assetUid={assetUid}
        interventionUid={detailUid}
        open={!!detailUid}
        onOpenChange={(o) => !o && setDetailUid(null)}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('interventions.delete.title', { defaultValue: 'Supprimer cette intervention ?' })}</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.reference} — {toDelete?.title}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('interventions.action.cancel', { defaultValue: 'Annuler' })}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) deleteMut.mutate(toDelete.uid)
                setToDelete(null)
              }}
            >
              {t('interventions.action.delete', { defaultValue: 'Supprimer' })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function MetricCard({
  icon,
  tone,
  value,
  label,
  testid,
}: {
  icon: string
  tone: 'info' | 'warning' | 'success' | 'muted'
  value: number | string
  label: string
  testid: string
}) {
  const toneColor =
    tone === 'info'
      ? 'text-info'
      : tone === 'warning'
        ? 'text-warning'
        : tone === 'success'
          ? 'text-success'
          : 'text-muted-foreground'
  return (
    <div className="rounded-lg border border-border bg-card p-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <AppIcon path={icon} size="md" className={toneColor} />
        <span className={`text-xl font-semibold ${toneColor}`}>{value}</span>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{label}</div>
    </div>
  )
}

function InterventionDialog({
  open,
  onOpenChange,
  assetUid,
  contracts,
  editing,
  onCreate,
  onUpdate,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  assetUid: string
  contracts: ContractLite[]
  editing: Intervention | null
  onCreate: (input: CreateInterventionInput) => void
  onUpdate: (uid: string, input: Partial<CreateInterventionInput>) => void
}) {
  const { t } = useTranslation('assets')
  const [title, setTitle] = useState('')
  const [kind, setKind] = useState<InterventionKind>('corrective')
  const [status, setStatus] = useState<InterventionStatus>('requested')
  const [scheduledAt, setScheduledAt] = useState('')
  const [performedAt, setPerformedAt] = useState('')
  const [costEur, setCostEur] = useState('')
  const [contractUid, setContractUid] = useState('')
  const [orderRef, setOrderRef] = useState('')
  const [description, setDescription] = useState('')

  // Sync form when the dialog opens or the editing target changes.
  const key = (open ? 'open' : 'closed') + (editing?.uid ?? 'new')
  const [syncedKey, setSyncedKey] = useState('')
  if (key !== syncedKey) {
    setSyncedKey(key)
    setTitle(editing?.title ?? '')
    setKind(editing?.kind ?? 'corrective')
    setStatus(editing?.status ?? 'requested')
    setScheduledAt(editing?.scheduled_at ? editing.scheduled_at.slice(0, 16) : '')
    setPerformedAt(editing?.performed_at ? editing.performed_at.slice(0, 16) : '')
    setCostEur(editing?.cost_amount != null ? String(editing.cost_amount / 100) : '')
    setContractUid(editing?.contract_uid ?? '')
    setOrderRef(editing?.order_ref ?? '')
    setDescription(editing?.description ?? '')
  }

  const submit = () => {
    const cost = costEur ? Math.round(parseFloat(costEur) * 100) : null
    const base = {
      kind,
      status,
      title,
      description,
      scheduled_at: toISO(scheduledAt),
      performed_at: toISO(performedAt),
      cost_amount: cost,
      cost_currency: cost != null ? 'EUR' : null,
      contract_uid: contractUid || null,
      order_ref: orderRef,
    }
    if (editing) {
      onUpdate(editing.uid, base)
    } else {
      onCreate({ asset_uid: assetUid, ...base })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {editing
              ? t('interventions.dialog.edit_title', { defaultValue: 'Éditer l’intervention' })
              : t('interventions.dialog.create_title', { defaultValue: 'Planifier une intervention' })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t('interventions.field.title', { defaultValue: 'Intitulé' })}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="intervention-form-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('interventions.field.kind', { defaultValue: 'Type' })}</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as InterventionKind)}>
                <SelectTrigger data-testid="intervention-form-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => (
                    <SelectItem key={k} value={k}>{t(`interventions.kind.${k}`, { defaultValue: k })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('interventions.field.status', { defaultValue: 'Statut' })}</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as InterventionStatus)}>
                <SelectTrigger data-testid="intervention-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{t(`interventions.status.${s}`, { defaultValue: s })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('interventions.field.scheduled_at', { defaultValue: 'Date planifiée' })}</Label>
              <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="intervention-form-scheduled" />
            </div>
            <div>
              <Label>{t('interventions.field.performed_at', { defaultValue: 'Date réalisée' })}</Label>
              <Input type="datetime-local" value={performedAt} onChange={(e) => setPerformedAt(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('interventions.field.cost', { defaultValue: 'Coût (€)' })}</Label>
              <Input type="number" value={costEur} onChange={(e) => setCostEur(e.target.value)} data-testid="intervention-form-cost" />
            </div>
            <div>
              <Label>{t('interventions.field.order_ref', { defaultValue: 'N° de commande' })}</Label>
              <Input value={orderRef} onChange={(e) => setOrderRef(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>{t('interventions.field.contract', { defaultValue: 'Contrat de maintenance' })}</Label>
            <Select value={contractUid || 'none'} onValueChange={(v) => setContractUid(v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="intervention-form-contract"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('interventions.field.no_contract', { defaultValue: 'Aucun (sur commande)' })}</SelectItem>
                {contracts.map((c) => (
                  <SelectItem key={c.uid} value={c.uid}>
                    {(c.name || c.code || c.uid.slice(0, 8))}
                    {c.billing_model ? ` · ${t(`interventions.billing_model.${c.billing_model}`, { defaultValue: c.billing_model })}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('interventions.field.description', { defaultValue: 'Notes' })}</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('interventions.action.cancel', { defaultValue: 'Annuler' })}
          </Button>
          <Button onClick={submit} disabled={!title} data-testid="intervention-form-submit">
            {t('interventions.action.save', { defaultValue: 'Enregistrer' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default InterventionsTab
